import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { type ApiError, Utils } from '@pbt/pbt-ui-components'

import {
  type CompletedRefundTransaction,
  type FailedRefundTransaction,
  type Payment as GraphQlPayment,
  type RefundInvoice,
  useInitiateCancelTransactionMutation,
  useInitiateRefundTransactionMutation,
} from '~/api/graphql/generated/types'
import {
  triggerErrorDoggo,
  triggerErrorDoggoForTransactionFailureIfNeeded,
} from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/utils/errorDoggoUtils'
import { extractBackendErrorMessage } from '~/components/dashboard/invoices/payment/add-payment-dialog-v2/utils/paymentDialogUtils'
import { PaymentMethod } from '~/constants/addPaymentDialogV2Constants'
import PaymentType from '~/constants/paymentTypes'
import i18n from '~/locales/i18n'
import { refreshBalanceHistory } from '~/store/actions/finance'
import { clearPaymentStoreError, createPayment } from '~/store/actions/payments'
import { fetchRefundInvoice } from '~/store/duck/refunds'
import {
  getCurrentBusiness,
  getCurrentBusinessId,
  getCurrentUser,
} from '~/store/reducers/auth'
import { getPaymentMethod, getPaymentType } from '~/store/reducers/constants'
import { fetchInvoiceV3 } from '~/store/reducers/invoiceV3'
import {
  getPaymentsError,
  getPaymentsIsLoading,
} from '~/store/reducers/payments'
import type { Payment, UnsavedExtendPayment } from '~/types'
import { getErrorMessage } from '~/utils/errors'
import usePrevious from '~/utils/usePrevious'

import { TransactionFailureReasonName } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'
import {
  type RefundDialogContextType,
  type RefundDialogStateType,
  RefundFlowState,
} from '../constants/refundDialogV2Constants'
import { initiateLinkedRefundTransaction } from '../utils/initiateLinkedRefundTransactionHelper'
import { initiateRefundTransaction } from '../utils/initiateRefundTransactionHelper'
import {
  calculateRefundableAmount,
  isPartialRefund,
} from '../utils/refundCalculations'
import {
  trackLinkedRefundFailure,
  trackLinkedRefundHardFailure,
  trackLinkedRefundStarted,
  trackLinkedRefundSuccess,
  trackRefundCancelTransactionClicked,
  trackRefundCancelTransactionFailure,
  trackRefundCancelTransactionSuccess,
  trackRefundInvoiceRefetch,
  trackUnlinkedRefundFailure,
  trackUnlinkedRefundHardFailure,
  trackUnlinkedRefundStarted,
  trackUnlinkedRefundSuccess,
} from '../utils/refundDialogTrackingUtils'
import {
  extractRefundFailureReasonName,
  getRefundTransactionFailureBackendMessage,
  getRefundTransactionFailureMessage,
} from '../utils/refundErrorMapping'

type RefundDialogAction =
  | { payload: RefundFlowState; type: 'SET_STATE' }
  | { type: 'CANCEL_TRANSACTION' }
  | { type: 'RESET_DIALOG' }
  | { payload: string | null; type: 'SET_CANCEL_SUCCESS_MESSAGE' }
  | {
      payload: { message: string } | null
      type: 'SET_CANCEL_ERROR'
    }
  | { payload: boolean; type: 'SET_IS_CANCELLING' }
  | { payload: string | null; type: 'SET_EXTERNAL_DEVICE_ID' }
  | {
      payload: {
        message: string
        severity: 'error' | 'warning'
        status: number
      }
      type: 'SET_REFUND_ERROR'
    }
  | { type: 'CLEAR_REFUND_ERROR' }
  | {
      payload: (GraphQlPayment | Payment) | null
      type: 'SET_SELECTED_PAYMENT'
    }
  | {
      payload: number | null
      type: 'SET_REFUND_AMOUNT'
    }
  | { payload: PaymentMethod; type: 'SET_REFUND_METHOD' }
  | { payload: string | null; type: 'SET_REFUND_CARD_TYPE' }
  | { payload: string | null; type: 'SET_REFUND_LAST_FOUR' }
  | {
      payload: { displayName: string; id: string }
      type: 'SET_SELECTED_PAYMENT_TERMINAL'
    }
  | { payload: string | null; type: 'SET_TRANSACTION_ID' }
  | { payload: boolean; type: 'SET_UNLINKED' }
  | { payload: boolean; type: 'SET_LINKED' }
  | { payload: boolean; type: 'SET_LINKED_REFUND_LISTENING' }
  | { payload: number; type: 'SET_PARTIAL_REFUND' }
  | { type: 'CLEAR_PARTIAL_REFUND' }
  | { payload: { totalRefunded: number }; type: 'RESET_FOR_PARTIAL_REFUND' }
  | { payload: number | null; type: 'SET_REFUND_START_TIME' }

type ActionHandlers = Record<
  RefundDialogAction['type'],
  (
    state: RefundDialogStateType,
    action: RefundDialogAction,
  ) => RefundDialogStateType
>

interface RefundDialogProviderProps {
  children: ReactNode
  refundInvoice: RefundInvoice
}

const createInitialDialogState = (): RefundDialogStateType => ({
  cancelError: null,
  cancelSuccessMessage: null,
  currentState: RefundFlowState.ISSUE_REFUND,
  externalDeviceId: null,
  hasPartialRefund: false,
  isCancelling: false,
  isLinkedRefundListening: false,
  linked: false,
  partialRefundAmount: 0,
  refundAmount: 0,
  refundCardType: null,
  refundError: null,
  refundLastFour: null,
  refundMethod: PaymentMethod.CREDIT_DEBIT,
  selectedPayment: null,
  selectedPaymentTerminal: null,
  selectedPaymentTerminalDisplayName: null,
  transactionId: null,
  unlinked: false,
  refundStartTime: null,
})

const actionHandlers: ActionHandlers = {
  SET_STATE: (state, action) => ({
    ...state,
    currentState: (action as { payload: RefundFlowState }).payload,
  }),
  CANCEL_TRANSACTION: (state) => ({
    ...state,
    currentState: RefundFlowState.ISSUE_REFUND,
    externalDeviceId: null,
    isCancelling: false,
    transactionId: null,
    refundStartTime: null,
  }),
  SET_CANCEL_SUCCESS_MESSAGE: (state, action) => ({
    ...state,
    cancelSuccessMessage: (action as { payload: string | null }).payload,
  }),
  SET_CANCEL_ERROR: (state, action) => ({
    ...state,
    cancelError: (
      action as {
        payload: { message: string } | null
      }
    ).payload,
  }),
  SET_IS_CANCELLING: (state, action) => ({
    ...state,
    isCancelling: (action as { payload: boolean }).payload,
  }),
  SET_EXTERNAL_DEVICE_ID: (state, action) => ({
    ...state,
    externalDeviceId: (action as { payload: string | null }).payload,
  }),
  RESET_DIALOG: (state) => ({
    ...state,
    ...createInitialDialogState(),
    cancelError: null,
    cancelSuccessMessage: null,
    refundStartTime: null,
  }),
  SET_REFUND_ERROR: (state, action) => ({
    ...state,
    refundError: (
      action as {
        payload: {
          message: string
          severity: 'error' | 'warning'
          status: number
        }
      }
    ).payload,
  }),
  CLEAR_REFUND_ERROR: (state) => ({
    ...state,
    refundError: null,
  }),
  SET_SELECTED_PAYMENT: (state, action) => ({
    ...state,
    selectedPayment: (
      action as {
        payload: (GraphQlPayment | Payment) | null
      }
    ).payload,
  }),
  SET_REFUND_AMOUNT: (state, action) => ({
    ...state,
    refundAmount: (
      action as {
        payload: number
      }
    ).payload,
  }),
  SET_REFUND_METHOD: (state, action) => ({
    ...state,
    refundMethod: (action as { payload: PaymentMethod }).payload,
  }),
  SET_REFUND_CARD_TYPE: (state, action) => ({
    ...state,
    refundCardType: (action as { payload: string | null }).payload,
  }),
  SET_REFUND_LAST_FOUR: (state, action) => ({
    ...state,
    refundLastFour: (action as { payload: string | null }).payload,
  }),
  SET_SELECTED_PAYMENT_TERMINAL: (state, action) => ({
    ...state,
    selectedPaymentTerminal: (
      action as { payload: { displayName: string; id: string } }
    ).payload.id,
    selectedPaymentTerminalDisplayName: (
      action as { payload: { displayName: string; id: string } }
    ).payload.displayName,
  }),
  SET_TRANSACTION_ID: (state, action) => ({
    ...state,
    transactionId: (action as { payload: string | null }).payload,
  }),
  SET_UNLINKED: (state, action) => ({
    ...state,
    unlinked: (action as { payload: boolean }).payload,
  }),
  SET_LINKED: (state, action) => ({
    ...state,
    linked: (action as { payload: boolean }).payload,
  }),
  SET_LINKED_REFUND_LISTENING: (state, action) => ({
    ...state,
    isLinkedRefundListening: (action as { payload: boolean }).payload,
  }),
  SET_PARTIAL_REFUND: (state, action) => ({
    ...state,
    hasPartialRefund: true,
    partialRefundAmount: (action as { payload: number }).payload,
  }),
  CLEAR_PARTIAL_REFUND: (state) => ({
    ...state,
    hasPartialRefund: false,
    partialRefundAmount: 0,
  }),
  RESET_FOR_PARTIAL_REFUND: (state, action) => {
    const { totalRefunded } = (
      action as {
        payload: { totalRefunded: number }
      }
    ).payload
    return {
      ...state,
      currentState: RefundFlowState.ISSUE_REFUND,
      hasPartialRefund: true,
      isLinkedRefundListening: false,
      linked: false,
      partialRefundAmount: totalRefunded,
      refundAmount: 0,
      refundCardType: null,
      refundMethod: PaymentMethod.CREDIT_DEBIT,
      refundLastFour: null,
      selectedPayment: null,
      selectedPaymentTerminal: null,
      selectedPaymentTerminalDisplayName: null,
      transactionId: null,
      unlinked: false,
      refundStartTime: null,
    }
  },
  SET_REFUND_START_TIME: (state, action) => ({
    ...state,
    refundStartTime: (action as { payload: number | null }).payload,
  }),
}

const refundDialogReducer = (
  state: RefundDialogStateType,
  action: RefundDialogAction,
): RefundDialogStateType => {
  // Handle case where state is undefined (can happen in test environments)
  if (!state) {
    return createInitialDialogState()
  }
  const handler = actionHandlers[action.type]
  return handler ? handler(state, action) : state
}

const RefundDialogContext = createContext<RefundDialogContextType | undefined>(
  undefined,
)

export const RefundDialogProvider = ({
  children,
  refundInvoice,
}: RefundDialogProviderProps) => {
  const [dialogState, dispatch] = useReducer(
    refundDialogReducer,
    createInitialDialogState(),
  )
  const [isLoading, setIsLoading] = useState(false)

  const [isWaitingForInvoiceRefresh, setIsWaitingForInvoiceRefresh] =
    useState(false)

  const [pendingRefundData, setPendingRefundData] = useState<{
    refundAmount: number
    selectedPayment: GraphQlPayment | Payment
  } | null>(null)
  const businessId = useSelector(getCurrentBusinessId) || ''
  const PaymentTypeConstant = useSelector(getPaymentType)
  const PaymentMethodConstant = useSelector(getPaymentMethod)
  const currentUser = useSelector(getCurrentUser)
  const currentBusiness = useSelector(getCurrentBusiness)
  const reduxDispatch = useDispatch()
  const { t } = useTranslation(['Dialogs'])

  // Redux state selectors for payment operations
  const paymentError = useSelector(getPaymentsError)
  const paymentLoading = useSelector(getPaymentsIsLoading)
  const wasPaymentLoading = usePrevious(paymentLoading)

  const [initiateRefundTransactionMutation] =
    useInitiateRefundTransactionMutation()
  const [initiateCancelTransactionMutation] =
    useInitiateCancelTransactionMutation()

  // Use ref to access latest dialogState in actions without causing re-renders
  const dialogStateRef = useRef(dialogState)
  const cancelStartTimeRef = useRef<number | null>(null)
  useEffect(() => {
    dialogStateRef.current = dialogState
  }, [dialogState])

  useEffect(() => {
    if (!dialogState.isCancelling && cancelStartTimeRef.current !== null) {
      cancelStartTimeRef.current = null
    }
  }, [dialogState.isCancelling])

  const PaymentMethodsToIdsMap = useMemo(
    () => ({
      [PaymentMethod.CASH]: `${Utils.findConstantIdByName('Cash', PaymentMethodConstant)}`,
      [PaymentMethod.CREDIT_DEBIT]: `${Utils.findConstantIdByName('Credit card', PaymentMethodConstant)}`,
      [PaymentMethod.CHECK]: `${Utils.findConstantIdByName('Check', PaymentMethodConstant)}`,
      [PaymentMethod.CARECREDIT]: `${Utils.findConstantIdByName('CareCredit', PaymentMethodConstant)}`,
    }),
    [PaymentMethodConstant],
  )

  // Track previous refundable amount to detect refunds made in other tabs
  const previousRefundableAmountRef = useRef(
    calculateRefundableAmount(refundInvoice),
  )

  const previousPaymentsLengthRef = useRef(refundInvoice.payments.length ?? 0)

  const handlePaymentSuccessState = (
    currentPendingRefundData: {
      refundAmount: number
      selectedPayment: GraphQlPayment | Payment
    },
    currentRefundInvoice: RefundInvoice,
  ) => {
    // TODO: Determine difference between amount and paidAmountNoFee for 3P refunds
    const partialRefund = isPartialRefund(
      currentPendingRefundData.refundAmount,
      currentRefundInvoice,
    )

    if (partialRefund) {
      // This is a partial refund - store the current refund amount
      dispatch({
        type: 'RESET_FOR_PARTIAL_REFUND',
        payload: {
          totalRefunded: currentPendingRefundData.refundAmount,
        },
      })
    } else {
      // This is a full refund
      dispatch({ type: 'CLEAR_PARTIAL_REFUND' })
      dispatch({ type: 'SET_STATE', payload: RefundFlowState.REFUND_SUCCESS })
    }
    setIsLoading(false)
    setPendingRefundData(null)
  }

  // Listen for Refund success/failure states
  // Only react to errors/success if we have pending refund data (i.e., we initiated the action)
  useEffect(() => {
    const currentRefundableAmount = calculateRefundableAmount(refundInvoice)
    const previousRefundableAmount = previousRefundableAmountRef.current

    if (paymentError && pendingRefundData) {
      // For manual refund errors from Redux, use generic connectivity message
      // as we don't have failure reason ID information from the API error
      const errorMessage = i18n.t(
        'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
      )

      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })
      triggerErrorDoggo(
        reduxDispatch,
        errorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      setIsLoading(false)
      setPendingRefundData(null)
      setIsWaitingForInvoiceRefresh(false)
    } else if (
      !paymentLoading &&
      wasPaymentLoading &&
      !paymentError &&
      pendingRefundData
    ) {
      // Refund completed successfully in this tab - move to success state
      handlePaymentSuccessState(pendingRefundData, refundInvoice)
      setIsWaitingForInvoiceRefresh(true)

      trackRefundInvoiceRefetch({
        transactionId: dialogState.transactionId,
        refundInvoiceId: refundInvoice.id,
        originalInvoiceId: refundInvoice.originalInvoiceId,
        businessId,
        clientId: refundInvoice.client?.id || null,
        refetchReason: 'MANUAL_REFUND_SUCCESS',
      })

      reduxDispatch(fetchRefundInvoice({ id: refundInvoice.id }))
      reduxDispatch(fetchInvoiceV3({ id: refundInvoice.originalInvoiceId }))
      if (refundInvoice.client?.id) {
        reduxDispatch(refreshBalanceHistory(refundInvoice.client.id))
      }
    } else if (
      // Detect partial refund made in another tab or external source
      previousRefundableAmount !== undefined &&
      currentRefundableAmount < previousRefundableAmount &&
      currentRefundableAmount > 0 &&
      !paymentLoading &&
      !pendingRefundData &&
      dialogState.currentState === RefundFlowState.ISSUE_REFUND
    ) {
      dispatch({
        type: 'RESET_FOR_PARTIAL_REFUND',
        payload: {
          totalRefunded: previousRefundableAmount - currentRefundableAmount,
        },
      })
    }

    const currentPaymentsLength = refundInvoice.payments.length ?? 0
    const previousPaymentsLength = previousPaymentsLengthRef.current

    if (
      isWaitingForInvoiceRefresh &&
      previousPaymentsLength !== undefined &&
      currentPaymentsLength !== previousPaymentsLength
    ) {
      setIsWaitingForInvoiceRefresh(false)
    }

    previousRefundableAmountRef.current = currentRefundableAmount
    previousPaymentsLengthRef.current = currentPaymentsLength
  }, [
    paymentError,
    paymentLoading,
    wasPaymentLoading,
    pendingRefundData,
    dialogState.currentState,
    refundInvoice,
    isWaitingForInvoiceRefresh,
    dispatch,
  ])
  const actions = useMemo(
    () => ({
      cancelTransaction: async () => {
        const currentDialogState = dialogStateRef.current
        const {
          externalDeviceId,
          transactionId,
          refundAmount,
          selectedPayment,
          selectedPaymentTerminal,
        } = currentDialogState

        if (!externalDeviceId || !transactionId) {
          return
        }

        trackRefundCancelTransactionClicked({
          refundInvoice,
          transactionId,
          refundAmount,
          businessId,
          selectedPaymentId: selectedPayment?.id || null,
          terminalId: selectedPaymentTerminal || null,
        })

        dispatch({
          type: 'SET_IS_CANCELLING',
          payload: true,
        })
        setIsLoading(true)
        dispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })
        dispatch({
          type: 'SET_CANCEL_SUCCESS_MESSAGE',
          payload: null,
        })

        cancelStartTimeRef.current = Date.now()

        try {
          await initiateCancelTransactionMutation({
            variables: {
              input: {
                externalDeviceId,
                transactionId,
              },
            },
          })
        } catch (error) {
          trackRefundCancelTransactionFailure({
            refundInvoice,
            transactionId,
            refundAmount,
            businessId,
            selectedPaymentId: selectedPayment?.id || null,
            terminalId: selectedPaymentTerminal || null,
            errorMessage: getErrorMessage(error as ApiError),
            cancelStartTime: cancelStartTimeRef.current,
          })
          triggerErrorDoggo(
            reduxDispatch,
            getErrorMessage(error as ApiError),
            500,
            currentUser,
            currentBusiness,
          )
          dispatch({
            type: 'SET_CANCEL_ERROR',
            payload: {
              message: t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_ERROR'),
            },
          })
          dispatch({
            type: 'SET_IS_CANCELLING',
            payload: false,
          })
          cancelStartTimeRef.current = null
        } finally {
          setIsLoading(false)
        }
      },
      resetDialog: () => {
        setIsLoading(false)
        setPendingRefundData(null)
        setIsWaitingForInvoiceRefresh(false)
        cancelStartTimeRef.current = null
        dispatch({ type: 'RESET_DIALOG' })
      },
      setRefundError: (refundError: {
        message: string
        severity: 'error' | 'warning'
        status: number
      }) => dispatch({ type: 'SET_REFUND_ERROR', payload: refundError }),
      clearRefundError: () => {
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        reduxDispatch(clearPaymentStoreError())
      },
      setRefundAmount: (amount: number) =>
        dispatch({ type: 'SET_REFUND_AMOUNT', payload: amount }),
      setRefundMethod: (method: PaymentMethod) =>
        dispatch({ type: 'SET_REFUND_METHOD', payload: method }),
      setSelectedPaymentTerminal: (terminalId: string, displayName: string) =>
        dispatch({
          type: 'SET_SELECTED_PAYMENT_TERMINAL',
          payload: { id: terminalId, displayName },
        }),
      setTransactionId: (transactionId: string | null) =>
        dispatch({
          type: 'SET_TRANSACTION_ID',
          payload: transactionId,
        }),
      setExternalDeviceId: (externalDeviceId: string | null) =>
        dispatch({
          type: 'SET_EXTERNAL_DEVICE_ID',
          payload: externalDeviceId,
        }),
      setSelectedPayment: (payment: GraphQlPayment | Payment | null) => {
        dispatch({ type: 'SET_SELECTED_PAYMENT', payload: payment })
        if (!payment) {
          dispatch({ type: 'SET_UNLINKED', payload: false })
          dispatch({ type: 'SET_LINKED', payload: false })
          dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
          return
        }

        const hasCardDetails = Boolean(payment.cardType && payment.lastFour)
        const graphqlPayment = payment as GraphQlPayment

        const hasPosTxIdOrGoTxId = Boolean(
          graphqlPayment.posTxId || graphqlPayment.goTxId,
        )

        if (hasCardDetails) {
          dispatch({
            type: 'SET_REFUND_METHOD',
            payload: PaymentMethod.CREDIT_DEBIT,
          })
          dispatch({
            type: 'SET_SELECTED_PAYMENT_TERMINAL',
            payload: { id: '', displayName: '' },
          })

          if (hasPosTxIdOrGoTxId) {
            // Unlinked refund flow: payment has posTxId or goTxId
            dispatch({ type: 'SET_UNLINKED', payload: true })
            dispatch({ type: 'SET_LINKED', payload: false })
          } else {
            // Linked refund flow: card payment without posTxId/goTxId
            dispatch({ type: 'SET_LINKED', payload: true })
            dispatch({ type: 'SET_UNLINKED', payload: false })
          }
        } else {
          // Non-card payment flow: Manual refund flow
          dispatch({ type: 'SET_UNLINKED', payload: false })
          dispatch({ type: 'SET_LINKED', payload: false })
        }
        dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
      },
      setState: (state: RefundFlowState) =>
        dispatch({ type: 'SET_STATE', payload: state }),
      submitManualRefund: (currentDialogState: RefundDialogStateType) => {
        dispatch({
          type: 'SET_CANCEL_SUCCESS_MESSAGE',
          payload: null,
        })
        dispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })
        dispatch({
          type: 'SET_IS_CANCELLING',
          payload: false,
        })
        if (!currentDialogState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }

        setIsLoading(true)
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        dispatch({ type: 'CLEAR_PARTIAL_REFUND' })
        reduxDispatch(clearPaymentStoreError())

        const { selectedPayment } = currentDialogState
        const paymentMethodId = selectedPayment.method?.id
        const clientId = refundInvoice.client?.id

        // Track pending refund to ensure we only react to our own actions
        setPendingRefundData({
          refundAmount: currentDialogState.refundAmount,
          selectedPayment,
        })
        const refundPayment: Partial<UnsavedExtendPayment> = {
          amount: currentDialogState.refundAmount,
          originPaymentId: selectedPayment.id,
          paidByPersonId: clientId,
          paymentMethodId: currentDialogState.unlinked
            ? PaymentMethodsToIdsMap[currentDialogState.refundMethod]
            : paymentMethodId,
          paymentTypeId: Utils.findConstantIdByName(
            PaymentType.REVERSE_CHARGE,
            PaymentTypeConstant,
          ),
          refundInvoiceId: refundInvoice.id,
          reversed: false,
        }

        reduxDispatch(createPayment(clientId, refundPayment))
      },
      submitUnlinkedRefund: async (
        currentDialogState: RefundDialogStateType,
      ) => {
        dispatch({
          type: 'SET_CANCEL_SUCCESS_MESSAGE',
          payload: null,
        })
        dispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })
        dispatch({
          type: 'SET_IS_CANCELLING',
          payload: false,
        })
        if (!currentDialogState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }
        setIsLoading(true)
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        dispatch({ type: 'CLEAR_PARTIAL_REFUND' })
        reduxDispatch(clearPaymentStoreError())

        const startTime = Date.now()
        dispatch({
          type: 'SET_REFUND_START_TIME',
          payload: startTime,
        })
        trackUnlinkedRefundStarted({
          refundInvoice,
          selectedPayment: currentDialogState.selectedPayment,
          refundAmount: currentDialogState.refundAmount,
          businessId,
          terminalId: currentDialogState.selectedPaymentTerminal,
        })

        await initiateRefundTransaction({
          refundInvoice,
          dispatch,
          reduxDispatch,
          initiateRefundTransactionMutation,
          refundAmount: currentDialogState.refundAmount,
          selectedPaymentId: currentDialogState.selectedPayment.id,
          selectedPaymentTerminal: currentDialogState.selectedPaymentTerminal,
          setIsLoading,
          currentUser,
          currentBusiness,
        })
      },
      submitLinkedRefund: async (currentDialogState: RefundDialogStateType) => {
        dispatch({
          type: 'SET_CANCEL_SUCCESS_MESSAGE',
          payload: null,
        })
        dispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })
        dispatch({
          type: 'SET_IS_CANCELLING',
          payload: false,
        })
        if (!currentDialogState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }
        setIsLoading(true)
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        dispatch({ type: 'CLEAR_PARTIAL_REFUND' })
        reduxDispatch(clearPaymentStoreError())

        const startTime = Date.now()
        dispatch({
          type: 'SET_REFUND_START_TIME',
          payload: startTime,
        })
        trackLinkedRefundStarted({
          refundInvoice,
          selectedPayment: currentDialogState.selectedPayment,
          refundAmount: currentDialogState.refundAmount,
          businessId,
        })

        await initiateLinkedRefundTransaction({
          dispatch,
          reduxDispatch,
          initiateRefundTransactionMutation,
          refundAmount: currentDialogState.refundAmount,
          refundInvoice,
          selectedPaymentId: currentDialogState.selectedPayment.id,
          setIsLoading,
          t,
          currentUser,
          currentBusiness,
        })
      },
      handleRefundTransactionSuccess: (
        currentRefundState: RefundDialogStateType,
        transaction: CompletedRefundTransaction,
      ) => {
        if (!currentRefundState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }
        const { isCancelling } = dialogStateRef.current
        const { selectedPayment } = currentRefundState
        const refundAmount = transaction.amount

        if (isCancelling) {
          const cancelTransactionId =
            currentRefundState.transactionId ?? transaction.id ?? ''
          trackRefundCancelTransactionFailure({
            refundInvoice,
            transactionId: cancelTransactionId,
            refundAmount: transaction.amount,
            businessId,
            selectedPaymentId: currentRefundState.selectedPayment?.id || null,
            terminalId: currentRefundState.selectedPaymentTerminal || null,
            errorMessage: t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_ERROR'),
            cancelStartTime: cancelStartTimeRef.current,
          })
          cancelStartTimeRef.current = null

          const partialRefund = isPartialRefund(refundAmount, refundInvoice)
          if (partialRefund) {
            dispatch({
              type: 'SET_CANCEL_ERROR',
              payload: {
                message: t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_ERROR'),
              },
            })
            dispatch({
              type: 'SET_IS_CANCELLING',
              payload: false,
            })
            dispatch({ type: 'CANCEL_TRANSACTION' })
            setIsWaitingForInvoiceRefresh(true)

            trackRefundInvoiceRefetch({
              transactionId: transaction.id,
              refundInvoiceId: refundInvoice.id,
              originalInvoiceId: refundInvoice.originalInvoiceId,
              refetchReason: 'CANCELLED_TRANSACTION_PARTIAL_REFUND',
              businessId,
              clientId: refundInvoice.client?.id || null,
            })

            reduxDispatch(fetchRefundInvoice({ id: refundInvoice.id }))
            reduxDispatch(
              fetchInvoiceV3({ id: refundInvoice.originalInvoiceId }),
            )
            if (refundInvoice.client?.id) {
              reduxDispatch(refreshBalanceHistory(refundInvoice.client.id))
            }
            return
          }
          dispatch({
            type: 'SET_IS_CANCELLING',
            payload: false,
          })
        }

        // For unlinked refunds, store the card details from the transaction since it will be on a new card (potentially)
        if (transaction.cardDetails && currentRefundState.unlinked) {
          dispatch({
            type: 'SET_REFUND_CARD_TYPE',
            payload: transaction.cardDetails.type?.name || null,
          })
          dispatch({
            type: 'SET_REFUND_LAST_FOUR',
            payload: transaction.cardDetails.lastFour || null,
          })
        }

        // Track refund success
        if (currentRefundState.unlinked) {
          trackUnlinkedRefundSuccess({
            refundInvoice,
            selectedPaymentId: currentRefundState.selectedPayment?.id || null,
            transaction,
            businessId,
            terminalId: currentRefundState.selectedPaymentTerminal,
            refundStartTime: currentRefundState.refundStartTime,
          })
        } else if (currentRefundState.linked) {
          trackLinkedRefundSuccess({
            refundInvoice,
            selectedPaymentId: currentRefundState.selectedPayment?.id || null,
            transaction,
            businessId,
            refundStartTime: currentRefundState.refundStartTime,
          })
        }

        // Move to success state
        dispatch({
          type: 'SET_STATE',
          payload: RefundFlowState.REFUND_SUCCESS,
        })
        handlePaymentSuccessState(
          { refundAmount, selectedPayment },
          refundInvoice,
        )
        setIsWaitingForInvoiceRefresh(true)

        const refetchReason = currentRefundState.unlinked
          ? 'UNLINKED_REFUND_SUCCESS'
          : currentRefundState.linked
            ? 'LINKED_REFUND_SUCCESS'
            : 'REFUND_SUCCESS'

        trackRefundInvoiceRefetch({
          refundInvoiceId: refundInvoice.id,
          originalInvoiceId: refundInvoice.originalInvoiceId,
          refetchReason,
          businessId,
          clientId: refundInvoice.client?.id || null,
          transactionId: transaction.id,
        })

        reduxDispatch(fetchRefundInvoice({ id: refundInvoice.id }))
        reduxDispatch(fetchInvoiceV3({ id: refundInvoice.originalInvoiceId }))
        if (refundInvoice.client?.id) {
          reduxDispatch(refreshBalanceHistory(refundInvoice.client.id))
        }
      },
      handleRefundTransactionFailure: (
        currentDialogState: RefundDialogStateType,
        transaction: FailedRefundTransaction,
      ) => {
        const { isCancelling } = dialogStateRef.current
        const failureReasonName = extractRefundFailureReasonName(transaction)
        const isCancelledByUser =
          failureReasonName === TransactionFailureReasonName.CANCELLED_BY_API
        const isCancelledByDevice =
          failureReasonName === TransactionFailureReasonName.CANCELLED_BY_DEVICE

        if ((isCancelling && isCancelledByUser) || isCancelledByDevice) {
          const currentDialogState = dialogStateRef.current
          const cancelTransactionId =
            currentDialogState.transactionId ?? transaction.id ?? ''
          trackRefundCancelTransactionSuccess({
            refundInvoice,
            transactionId: cancelTransactionId,
            refundAmount: currentDialogState.refundAmount,
            businessId,
            selectedPaymentId: currentDialogState.selectedPayment?.id || null,
            terminalId: currentDialogState.selectedPaymentTerminal || null,
            cancelStartTime: cancelStartTimeRef.current,
          })
          cancelStartTimeRef.current = null
          dispatch({
            type: 'SET_CANCEL_SUCCESS_MESSAGE',
            payload: t(
              'Dialogs:REFUND_DIALOG_V2.CANCEL_SUCCESS_MESSAGE_WITHOUT_CREDITS',
            ),
          })
          dispatch({
            type: 'CLEAR_REFUND_ERROR',
          })
          dispatch({
            type: 'SET_IS_CANCELLING',
            payload: false,
          })
          dispatch({ type: 'CANCEL_TRANSACTION' })
          return
        }

        dispatch({ type: 'CANCEL_TRANSACTION' })
        const errorMessage = getRefundTransactionFailureMessage(
          transaction,
          currentDialogState.linked,
        )

        // Track refund failure
        if (currentDialogState.unlinked) {
          trackUnlinkedRefundFailure({
            refundInvoice,
            selectedPaymentId: currentDialogState.selectedPayment?.id || null,
            transaction,
            refundAmount: currentDialogState.refundAmount,
            businessId,
            terminalId: currentDialogState.selectedPaymentTerminal,
            failureReason: extractRefundFailureReasonName(transaction),
            refundStartTime: currentDialogState.refundStartTime,
          })
        } else if (currentDialogState.linked) {
          trackLinkedRefundFailure({
            refundInvoice,
            selectedPaymentId: currentDialogState.selectedPayment?.id || null,
            transaction,
            refundAmount: currentDialogState.refundAmount,
            businessId,
            failureReason: extractRefundFailureReasonName(transaction),
            refundStartTime: currentDialogState.refundStartTime,
          })
        }
        triggerErrorDoggoForTransactionFailureIfNeeded({
          failureReasonName,
          dispatch: reduxDispatch,
          backendErrorMessage:
            getRefundTransactionFailureBackendMessage(transaction),
          fallbackMessage: errorMessage,
          currentUser,
          currentBusiness,
        })
        dispatch({
          type: 'SET_REFUND_ERROR',
          payload: {
            message: errorMessage,
            severity: 'error',
            status: 500,
          },
        })

        // If linked refund failed, fall back to unlinked flow unless failure reason is UNKNOWN or TRANSACTION_NOT_SETTLED
        if (
          currentDialogState.linked &&
          extractRefundFailureReasonName(transaction) !==
            TransactionFailureReasonName.UNKNOWN &&
          extractRefundFailureReasonName(transaction) !==
            TransactionFailureReasonName.TRANSACTION_NOT_SETTLED
        ) {
          dispatch({ type: 'SET_LINKED', payload: false })
          dispatch({ type: 'SET_UNLINKED', payload: true })
          dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
          dispatch({ type: 'SET_TRANSACTION_ID', payload: null })
        }
        setIsLoading(false)
        setIsWaitingForInvoiceRefresh(false)
      },
      handleRefundTransactionHardFailure: (
        currentDialogState: RefundDialogStateType,
        error?: any,
      ) => {
        dispatch({ type: 'CANCEL_TRANSACTION' })
        const errorMessage = i18n.t(
          'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_DOWN',
        )

        // Track refund hard failure
        if (currentDialogState.unlinked) {
          trackUnlinkedRefundHardFailure({
            refundInvoice,
            selectedPaymentId: currentDialogState.selectedPayment?.id || null,
            refundAmount: currentDialogState.refundAmount,
            businessId,
            terminalId: currentDialogState.selectedPaymentTerminal,
            transactionId: currentDialogState.transactionId,
            errorMessage: error?.message || null,
            refundStartTime: currentDialogState.refundStartTime,
          })
        } else if (currentDialogState.linked) {
          trackLinkedRefundHardFailure({
            refundInvoice,
            selectedPaymentId: currentDialogState.selectedPayment?.id || null,
            refundAmount: currentDialogState.refundAmount,
            businessId,
            transactionId: currentDialogState.transactionId,
            errorMessage: error?.message || null,
            refundStartTime: currentDialogState.refundStartTime,
          })
        }
        const backendErrorMessage = extractBackendErrorMessage(
          error,
          errorMessage,
        )
        triggerErrorDoggo(
          reduxDispatch,
          backendErrorMessage,
          500,
          currentUser,
          currentBusiness,
        )
        dispatch({
          type: 'SET_REFUND_ERROR',
          payload: {
            message: errorMessage,
            severity: 'error',
            status: 500,
          },
        })

        // If linked refund failed, fall back to unlinked flow
        if (currentDialogState.linked) {
          dispatch({ type: 'SET_LINKED', payload: false })
          dispatch({ type: 'SET_UNLINKED', payload: true })
          dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
          dispatch({ type: 'SET_TRANSACTION_ID', payload: null })
        }
        setIsLoading(false)
        setIsWaitingForInvoiceRefresh(false)
      },
    }),
    [
      dispatch,
      reduxDispatch,
      setIsLoading,
      dialogState.selectedPayment,
      dialogState.refundAmount,
      refundInvoice,
      businessId,
      PaymentTypeConstant,
      t,
      initiateRefundTransactionMutation,
      currentUser,
      currentBusiness,
    ],
  )

  const contextValue = useMemo(
    () => ({
      dialogState,
      isLoading,
      isWaitingForInvoiceRefresh,
      refundInvoice,
      actions,
    }),
    [
      dialogState,
      isLoading,
      isWaitingForInvoiceRefresh,
      refundInvoice,
      actions,
    ],
  )
  return (
    <RefundDialogContext.Provider value={contextValue}>
      {children}
    </RefundDialogContext.Provider>
  )
}

export const useRefundDialog = (): RefundDialogContextType => {
  const context = useContext(RefundDialogContext)
  if (!context) {
    throw new Error(
      'useRefundDialog must be used within a RefundDialogProvider',
    )
  }
  return context
}
